import React from "react";
import { Link } from "react-router-dom";
import { APP_NAME, SUPPORT_EMAIL } from "../constants/expiry";
import { SentrilogIcon } from "../components/Icons";

const EFFECTIVE_DATE = "March 1, 2026";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-[#111827] mb-4">{title}</h2>
    <div className="text-[#374151] text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const TermsOfService: React.FC = () => (
  <div className="min-h-screen bg-[#F9FAFB]">
    {/* Nav */}
    <header className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
            <SentrilogIcon className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-[#111827] text-lg">{APP_NAME}</span>
        </Link>
        <Link
          to="/"
          className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
        >
          ← Back to home
        </Link>
      </div>
    </header>

    {/* Content */}
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-3xl font-extrabold text-[#111827] mb-2">
          Terms of Service
        </h1>
        <p className="text-sm text-[#6B7280]">
          Effective date: {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] px-8 py-10">
        <p className="text-[#374151] text-sm leading-relaxed mb-8">
          These Terms of Service ("<strong>Terms</strong>") govern your access
          to and use of the {APP_NAME} platform and services (the "
          <strong>Service</strong>") provided by {APP_NAME} ("
          <strong>we</strong>", "<strong>us</strong>", or "
          <strong>our</strong>"). By creating an account or using the Service,
          you agree to be bound by these Terms. If you are using the Service on
          behalf of an organisation, you represent that you have authority to
          bind that organisation to these Terms.
        </p>

        <Section title="1. The Service">
          <p>
            {APP_NAME} is a document expiry tracking and compliance reminder
            platform. The Service allows users to upload documents, set expiry
            dates, manage team workspaces, and receive automated reminders via
            email, Slack, and other supported channels.
          </p>
          <p>
            We reserve the right to modify, suspend, or discontinue any part of
            the Service at any time with reasonable notice. We will not be
            liable to you or any third party for any such modification,
            suspension, or discontinuation.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <p>
            You must be at least 16 years of age to use the Service. By using
            the Service, you represent and warrant that you meet this
            requirement. If you are accepting these Terms on behalf of a company
            or other legal entity, you represent that you have the authority to
            bind that entity to these Terms.
          </p>
        </Section>

        <Section title="3. Accounts">
          <p>
            You are responsible for maintaining the confidentiality of your
            account credentials and for all activities that occur under your
            account. You agree to:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Provide accurate, current, and complete registration information.</li>
            <li>Promptly update your information if it changes.</li>
            <li>
              Notify us immediately at{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-[#2563EB] underline"
              >
                {SUPPORT_EMAIL}
              </a>{" "}
              if you suspect unauthorised access to your account.
            </li>
            <li>
              Not share your password with any third party or allow others to
              access your account.
            </li>
          </ul>
          <p className="mt-3">
            We reserve the right to terminate or suspend accounts that violate
            these Terms or that we determine, in our sole discretion, pose a
            risk to the security or integrity of the Service.
          </p>
        </Section>

        <Section title="4. Subscriptions and Payment">
          <p>
            {APP_NAME} offers both free and paid subscription plans. Paid plans
            are billed in advance on a monthly or annual basis. All fees are
            non-refundable except as expressly stated in our Refund Policy or
            as required by applicable law.
          </p>
          <p>
            If you upgrade your plan, you will be charged the new plan rate at
            the beginning of the next billing cycle, or pro-rated for the
            current cycle at our discretion. If you downgrade, changes will take
            effect at the start of the next billing cycle.
          </p>
          <p>
            We may change our pricing at any time. We will provide at least 30
            days' advance notice of price increases via email or in-app
            notification. Your continued use of the Service after a price change
            takes effect constitutes your agreement to the new pricing.
          </p>
          <p>
            Failure to pay may result in suspension or termination of your
            account. All payments are processed securely by Stripe.
          </p>
        </Section>

        <Section title="5. Acceptable Use">
          <p>You agree not to use the Service to:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              Violate any applicable law, regulation, or third-party rights.
            </li>
            <li>
              Upload, store, or transmit any unlawful, harmful, defamatory,
              obscene, or otherwise objectionable content.
            </li>
            <li>
              Attempt to gain unauthorised access to any part of the Service or
              its related systems.
            </li>
            <li>
              Reverse engineer, decompile, or disassemble any part of the
              Service.
            </li>
            <li>
              Use the Service to send spam, unsolicited communications, or
              automated queries not permitted by these Terms.
            </li>
            <li>
              Interfere with or disrupt the integrity or performance of the
              Service.
            </li>
            <li>
              Use the Service for any purpose that competes with {APP_NAME}
              without our prior written consent.
            </li>
          </ul>
          <p className="mt-3">
            We reserve the right to investigate any suspected violation of these
            terms and to take appropriate action, including removing content,
            suspending accounts, and cooperating with law enforcement.
          </p>
        </Section>

        <Section title="6. Your Content">
          <p>
            You retain all ownership rights to the documents and data you upload
            to the Service ("<strong>Your Content</strong>"). By uploading
            content, you grant us a limited, non-exclusive, royalty-free licence
            to store, process, and display Your Content solely for the purpose
            of providing the Service to you.
          </p>
          <p>
            You are solely responsible for Your Content and represent that you
            have all necessary rights to upload and use it within the Service.
            We do not claim ownership of Your Content and will not use it for
            any purpose beyond operating the Service.
          </p>
          <p>
            We may use anonymised and aggregated data derived from Your Content
            to improve our AI models and Service quality. Such data will never
            include personally identifiable information or be attributable to
            you or your organisation.
          </p>
        </Section>

        <Section title="7. Team Workspaces">
          <p>
            The Service supports shared team workspaces. The workspace
            administrator ("<strong>Admin</strong>") is responsible for managing
            user access, roles, and permissions. By inviting team members, the
            Admin acknowledges that those members may view, edit, or manage
            content within the shared workspace according to their assigned role.
          </p>
          <p>
            {APP_NAME} is not responsible for the actions of team members within
            a workspace. Admins are responsible for ensuring compliance with
            these Terms within their organisation.
          </p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>
            The Service and its original content, features, and functionality
            are and will remain the exclusive property of {APP_NAME} and its
            licensors. Our trademarks, trade names, and logos may not be used
            without our prior written permission.
          </p>
          <p>
            Any feedback, suggestions, or ideas you provide about the Service
            may be used by us without restriction or compensation to you.
          </p>
        </Section>

        <Section title="9. Disclaimer of Warranties">
          <p>
            THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
            WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING
            BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
            FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>
            WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
            ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. YOU USE
            THE SERVICE AT YOUR OWN RISK.
          </p>
          <p>
            {APP_NAME} is a reminder and tracking tool. We are not responsible
            for any consequences arising from a missed expiry, compliance
            failure, or reliance on data within the Service. You are solely
            responsible for verifying the accuracy of your data and for
            compliance with any applicable regulations.
          </p>
        </Section>

        <Section title="10. Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
            SHALL {APP_NAME.toUpperCase()}, ITS DIRECTORS, EMPLOYEES, PARTNERS,
            AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
            WITHOUT LIMITATION LOSS OF PROFITS, DATA, GOODWILL, OR OTHER
            INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF (OR
            INABILITY TO USE) THE SERVICE.
          </p>
          <p>
            OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR
            RELATING TO THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE
            AMOUNTS YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM OR (B)
            €100.
          </p>
        </Section>

        <Section title="11. Termination">
          <p>
            You may terminate your account at any time by contacting us at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-[#2563EB] underline"
            >
              {SUPPORT_EMAIL}
            </a>
            . Upon termination, your right to use the Service will immediately
            cease.
          </p>
          <p>
            We may terminate or suspend your account immediately, without prior
            notice, if you breach these Terms, engage in fraudulent activity, or
            if we are required to do so by law.
          </p>
          <p>
            Upon termination, provisions of these Terms that by their nature
            should survive will survive, including ownership provisions,
            disclaimers, and limitations of liability.
          </p>
        </Section>

        <Section title="12. Governing Law">
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of the European Union and the laws of the jurisdiction in
            which {APP_NAME} is registered, without regard to conflict of law
            principles. Any dispute arising from or relating to these Terms
            shall be subject to the exclusive jurisdiction of the courts in that
            jurisdiction.
          </p>
        </Section>

        <Section title="13. Changes to These Terms">
          <p>
            We reserve the right to update these Terms at any time. When we
            make material changes, we will notify you by email or via a
            prominent notice in the Service at least 14 days before the changes
            take effect. Your continued use of the Service after the effective
            date of updated Terms constitutes your acceptance of those Terms.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>
            If you have any questions about these Terms, please contact us:
          </p>
          <div className="mt-3 p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] text-sm">
            <p className="font-semibold text-[#111827]">{APP_NAME}</p>
            <p>
              Email:{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-[#2563EB] underline"
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p>Website: sentrilog.com</p>
          </div>
        </Section>
      </div>

      {/* Bottom links */}
      <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-[#6B7280]">
        <Link
          to="/privacy"
          className="hover:text-[#111827] transition-colors underline"
        >
          Privacy Policy
        </Link>
        <Link
          to="/cookie-policy"
          className="hover:text-[#111827] transition-colors underline"
        >
          Cookie Policy
        </Link>
        <Link
          to="/refund"
          className="hover:text-[#111827] transition-colors underline"
        >
          Refund Policy
        </Link>
        <Link
          to="/pricing"
          className="hover:text-[#111827] transition-colors underline"
        >
          Pricing
        </Link>
        <Link to="/" className="hover:text-[#111827] transition-colors">
          ← Back to home
        </Link>
      </div>
    </main>
  </div>
);

export default TermsOfService;
